<template>
    <div class="w-full" :class="notSmallScreen ? 'flex justify-start items-start ' : 'flex flex-col'">
        <div class="flex items-center w-3/12 space-x-2 h-8">
            <checkbox v-model="isSelected" @input="checkboxToggledEvent" :label="type | titlecase"
                      label-class="font-medium"></checkbox>
        </div>

        <div :class="notSmallScreen? 'w-9/12' : ''" v-show="isSelected">
            <div class="divide-y divide-gray-100 rounded-md">
                <!-- SAVED REPLY SELECTOR -->
                <div class="flex space-x-2 bg-white ring-1 ring-black ring-opacity-5 mb-0.5 rounded-md" ref="inputButtonContainer">
                    <div @click="displayPopup()" class="flex-1 truncate text-xs h-8 text-gray-800 placeholder-gray-400 placeholder-text-xs font-normal"
                         :class="notSmallScreen ? 'w-full' : 'w-48'">
                        <input v-if="!selectedSavedReply || (showPopup && !isEditing)" v-model="searchInput" type="text"
                               class="w-full h-full border-0 bg-transparent px-2 focus:ring-0"
                               placeholder="Search saved replies to send..." role="combobox" aria-expanded="false" aria-controls="options">
                        <div v-else-if="selectedSavedReply && (isEditing || !showPopup) && !selectingSavedReply" :title="selectedSavedReply.name"
                             class="w-full h-full border-0 bg-transparent px-2 focus:ring-0 text-center flex items-center cursor-pointer">
                            {{ useCustomBody ? "(Custom)" : "" }} {{ selectedSavedReply.name }}
                        </div>
                        <div v-else-if="selectingSavedReply"
                             class="w-full h-full border-0 bg-transparent px-2 focus:ring-0 text-gray-400 text-center flex items-center cursor-pointer">
                            Loading saved reply content
                        </div>
                    </div>

                    <div v-if="communicationChannel && communicationChannel.saved_reply_id && !selectingSavedReply" class="flex items-center space-x-1">
                        <!-- OPEN EDITING / ADVANCED EMAIL OPTIONS -->
                        <button v-if="communicationChannel && communicationChannel.saved_reply_id && !disableEditing" @click="enterEditing()"
                                class="outline-none flex items-center">
                            <icon-pencil-alt class="w-4 h-4  transition duration-300"
                                             :class="[editIconActive ? 'text-indigo-600 hover:text-indigo-800' : 'text-gray-400 hover:text-indigo-600']"></icon-pencil-alt>
                        </button>
                        <!-- CLEAR SAVED REPLY-->
                        <button @click="clearNotificationChannel(); hidePopup()"
                                class="outline-none flex items-center">
                            <icon-x-circle class="w-4 h-4 text-gray-400 hover:text-brand transition duration-300 mr-1"></icon-x-circle>
                        </button>
                    </div>
                </div>

                <div v-show="showPopup" ref="listPopup" class="shadow-lg bg-white z-50 -mt-1" style="min-height: 200px">
                    <!-- Results, show/hide based on command palette state -->
                    <ul v-if="!isEditing" role="listbox" :id="optionDivId"
                        class="scroll-py-3 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100 space-y-1 pl-2"
                        :class="browser === 'chrome' ? 'pr-4' : 'pr-2'">
                        <!-- More items... -->
                        <!-- Active: "bg-gray-100" -->
                        <li class="group flex cursor-pointer select-none rounded-md p-2 hover:bg-gray-100 transition duration-300"
                            :class="{'bg-indigo-50' : index % 2 === 1}"
                            role="option"
                            tabindex="-1"
                            v-for="(savedReply, index) in savedReplies"
                            @click="selectSavedReply(savedReply)">

                            <div class="flex-auto">
                                <!-- Active: "text-gray-900", Not Active: "text-gray-700" -->
                                <p class="text-sm font-medium text-gray-700 group-hover:text-indigo-600 transition duration-300">
                                    {{ savedReply.name }}</p>
                            </div>
                        </li>
                    </ul>

                    <!-- Empty state, show/hide based on command palette state -->
                    <div class="py-14 px-6 text-center text-sm sm:px-14" v-if="!loading && !isEditing && savedReplies.length === 0">
                        <icon-exclamation-circle class="mx-auto h-6 w-6 text-gray-400" aria-hidden="true"></icon-exclamation-circle>
                        <p class="mt-4 font-semibold text-gray-900">No results found</p>
                        <p class="mt-2 text-gray-500">No saved replies found for this search term. Please try again.</p>
                    </div>

                    <div v-if="loading && !isEditing" class="flex justify-center py-20">
                        <icon-refresh class="h-4 w-4 text-indigo-600 animate-spin"/> &nbsp;Loading Saved Replies...
                    </div>
                </div>

                <!-- EDITING SECTION -->
                <portal to="savedReplyNotificationInputPortal" v-if="isEditing && showPopup">
                    <div style="max-height: 31rem"
                         class="scroll-py-3 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100 pr-2">
                        <div class="mb-2 w-full space-y-2" v-if="communicationChannel.type === 'email' && !disableCcs">
                            <email-input
                                v-model="communicationChannel.cc"
                                :limit="10"
                                :typeahead="true"
                                :assigned-emails="assignedEmails"
                                input-prefix="Cc:  "
                                input-box-class="w-full text-xs input-normal rounded-right flex-col h-full"
                                suggestions-api="/contacts/suggestions/search"
                                element-id="cc-email">
                            </email-input>

                            <email-input
                                v-model="communicationChannel.bcc"
                                :limit="10"
                                :typeahead="true"
                                :assigned-emails="assignedEmails"
                                input-prefix="Bcc:"
                                input-box-class="w-full text-xs input-normal rounded-right flex-col h-full"
                                suggestions-api="/contacts/suggestions/search"
                                element-id="bcc-email">
                            </email-input>
                        </div>

                        <div class="flex flex-col">
                            <textarea
                                class="w-full text-sm border border-gray-300 rounded-md px-3 py-2 overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100"
                                v-model="communicationChannel.custom_body" style="max-height: 265px !important"
                                rows="8"></textarea>

                            <span v-show="editMessage" class="text-sm text-gray-600 mt-2 p-2 bg-gray-200 rounded-md">{{ editMessage }}</span>

                            <div class="mt-2" v-if="!disableAttachments">
                                <div v-for="attachment in communicationChannel.media"
                                     class="flex items-center justify-between mt-2 hover:bg-gray-50 rounded-md transition duration-300">
                                    <div class="flex items-center truncate">
                                        <span class="text-sm text-gray-600 font-medium flex items-center"
                                              v-if="attachment.size">{{ attachment.size }} &nbsp;&middot; &nbsp;</span>
                                        <span class="text-sm text-gray-600 font-normal truncate">{{ attachment.name }}</span>
                                    </div>
                                    <button class="outline-none text-gray-400 hover:text-brand transition duration-300"
                                            @click.prevent="removeSelectedAttachment(attachment)">
                                        <icon-x-circle class="w-4 h-4 ml-2"></icon-x-circle>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div :class="['flex flex-row md:items-center gap-x-4 mt-2 sticky bottom-0 bg-white',
                            communicationChannel.type === 'email' && multiUploadUrl ? 'md:justify-between' : 'md:justify-center']">
                        <button
                            v-if="multiUploadUrl && communicationChannel.type === 'email'" @click="toggleMultiAttachmentModal()"
                            class="flex items-center editor-attachment text-indigo-600 hover:text-indigo-800 transition duration-300"
                            :disabled="attachmentLimitReached">
                            <i class="mdi mdi-upload-multiple mr-1 mt-0.5"></i>Attach Existing Media
                        </button>

                        <button class="text-green-500 hover:text-green-700 transition duration-300 flex items-center justify-end text-sm p-2"
                                @click.prevent="leaveEditing()">
                            <icon-check class="w-4 h-4"></icon-check>
                            Done
                        </button>
                    </div>
                </portal>
            </div>
        </div>

        <portal to="contentOverlayPortal" v-if="multiUploadUrl && showMultiUploadModal">
            <multi-upload-component
                :show="showMultiUploadModal"
                :media-url="multiUploadUrl"
                :full-width="true"
                :model-type="populateForContactable.contactable_type"
                submit-label="Attach"
                additional-body-classes="mx-12"
                @media-selected="attachSelectedMedia"
                @close-modal="toggleMultiAttachmentModal()">
            </multi-upload-component>
        </portal>
    </div>
</template>

<script>
import { createPopper } from "@popperjs/core";

export default {
    name: "SavedReplyNotificationInput",

    props: {
        type: {
            required: true,
            type: String,
            description: "Used as type when fetching saved replies"
        },
        category: {
            required: false,
            type: String,
            description: "Used as category when fetching saved replies",
            default: "maintenance"
        },
        role: {
            required: true,
            type: String,
            description: "Used as role when fetching saved replies"
        },
        value: {
            required: false,
            type: Object,
            description: "Used to be able to v-model a saved reply"
        },
        editMessage: {
            required: false,
            type: String,
            description: "Used to show a message when editing a saved reply",
            default: null,
        },
        populateForContactable: {
            required: false,
            type: Object,
            description: "To populate saved reply when selecting it, required contactable type and id and service_provider_iD",
            default: () => {return {};},
        },
        toEmail: {
            required: false,
            type: String,
            description: "Used for the email-input to filter out the email from the CC or BCC emails"
        },
        disableEditing: {
            required: false,
            type: Boolean,
            description: "Disable the use of the Ccs and BCcs when showing the advanced options of the saved reply"
        },
        disableCcs: {
            required: false,
            type: Boolean,
            description: "Disable the use of the Ccs and BCcs when showing the advanced options of the saved reply"
        },
        disableAttachments: {
            required: false,
            type: Boolean,
            description: "Disable the attaching of media when showing the advanced options of the saved reply"
        },
    },

    data () {
        return {
            popperInstance: null,
            searchInput: null,
            communicationChannel: {
                type: this.type,
                saved_reply_id: null,
                custom_body: null,
                name: null,
                cc: [],
                bcc: [],
                media: [],
            },
            selectedSavedReply: null,
            selectingSavedReply: false,
            isEditing: false,
            loading: false,
            savedReplies: [],
            showPopup: false,
            optionDivId: "options-" + Math.random() * 999999,
            isSelected: false,
            showMultiUploadModal: false,
            attachmentLimit: 10,
        };
    },

    computed: {
        useCustomBody () {
            return this.selectedSavedReply && this.selectedSavedReply.body !== this.communicationChannel?.custom_body;
        },

        assignedEmails () {
            let emails = [];
            this.toEmail ? emails.push(this.toEmail) : null;
            if (this.communicationChannel && this.communicationChannel.cc) {
                this.communicationChannel.cc.forEach(function (cc) {
                    emails.push(cc.email);
                });
            }
            if (this.communicationChannel && this.communicationChannel.bcc) {
                this.communicationChannel.bcc.forEach(function (bcc) {
                    emails.push(bcc.email);
                });
            }
            return emails;
        },
        multiUploadUrl () {
            if (!_.isEmpty(this.populateForContactable) && this.populateForContactable.contactable_type && this.populateForContactable.contactable_id) {
                return "/attachments/multi/" + this.populateForContactable.contactable_type.substr(4).toLowerCase() + "/" + this.populateForContactable.contactable_id;
            }

            return null;
        },
        attachmentLimitReached () {
            return this.communicationChannel?.media?.length >= this.attachmentLimit;
        },

        editIconActive () {
            return this.communicationChannel &&
                ((this.communicationChannel.media && this.communicationChannel.media.length > 0) ||
                    (this.communicationChannel.cc && this.communicationChannel.cc.length > 0 || (this.communicationChannel.bcc && this.communicationChannel.bcc.length > 0)));
        },

        browser () {
            return this.currentBrowser();
        },
    },

    watch: {
        searchInput () {
            this.fetchSavedReplies();
        },
        communicationChannel: {
            handler (commChannel) {
                if (commChannel !== undefined) {
                    this.$emit("input", commChannel);
                }
            },
            deep: true
        },
        value (newVal) {
            if (newVal && newVal.id) {
                this.selectSavedReply(newVal);
            } else if (newVal === undefined || newVal === null) {
                this.isSelected = false;
                this.selectedSavedReply = null;
            }
        },
    },

    mounted () {
        if (this.value && this.value.id) {
            this.selectSavedReply(this.value);
        }

        let self = this;
        Bus.$on("close-saved-reply-popup", function () {
            if (self.showPopup) {
                self.hidePopup();
            }
        });
    },

    methods: {
        fetchSavedReplies: _.debounce(function () {
            this.loading = true;
            axios.get("/savedreplies/context/all", ({
                params: {
                    filter: this.searchInput,
                    role: this.role,
                    type: this.type === "messenger" ? "email" : this.type,
                    category: this.category,
                }
            }))
            .then(response => {
                this.savedReplies = response.data;
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
                window.toastr.error("Error retrieving saved replies.");
            });
        }, 300),

        selectSavedReply (savedReply) {
            this.selectingSavedReply = true;
            this.hidePopup();

            if (this.communicationChannel) {
                this.communicationChannel.name = savedReply.name;
                this.communicationChannel.type = this.type;
                this.communicationChannel.saved_reply_id = savedReply.id;
                this.communicationChannel.custom_body = savedReply.body;
            } else {
                this.communicationChannel = {
                    name: savedReply.name,
                    type: this.type,
                    saved_reply_id: savedReply.id,
                    custom_body: savedReply.body,
                };
            }

            this.selectedSavedReply = savedReply;
            this.isSelected = true;

            if (!_.isEmpty(this.communicationChannel) && !_.isEmpty(this.populateForContactable) && this.populateForContactable.contactable_type
                && this.populateForContactable.contactable_id && this.communicationChannel.saved_reply_id) {
                App.post("/savedreplies/context/populate", new AppForm({
                    contactable: this.populateForContactable.contactable_type,
                    contactable_id: this.populateForContactable.contactable_id,
                    selectedSavedReply: this.communicationChannel.saved_reply_id,
                    selectedServiceProvider: this.populateForContactable.service_provider_id ? this.populateForContactable.service_provider_id : null,
                    selectedContact: this.populateForContactable.selectedContact ? this.populateForContactable.selectedContact : null,
                    return_html: this.type === "messenger" || this.type === "email"
                })).then((response) => {
                    this.communicationChannel.custom_body = response;
                    this.$emit("input", this.communicationChannel);
                    savedReply.body = response;
                    savedReply.custom_body = response;
                    this.selectingSavedReply = false;
                }).catch((error) => {
                    this.selectingSavedReply = false;
                    toastr.error("Could Not Populate Message With Selected Saved Reply. Error: " + error.message);
                });
            } else {
                this.$emit("input", this.communicationChannel);
                this.selectingSavedReply = false;
            }
        },

        hidePopup () {
            if (document.getElementById(this.optionDivId)) {
                document.getElementById(this.optionDivId).scrollTo(0, 0);
            }
            this.showPopup = false;
            this.isEditing = false;
        },

        displayPopup () {
            Bus.$emit("close-saved-reply-popup");
            const button = this.$refs.inputButtonContainer;
            const popup = this.$refs.listPopup;

            this.showPopup = true;

            if (!this.popperInstance) {
                this.popperInstance = createPopper(button, popup, {
                    placement: "bottom-start",
                    modifiers: [
                        {
                            name: "sameWidth",
                            enabled: true,
                            phase: "beforeWrite",
                            requires: ["computeStyles"],
                            fn: ({state}) => {
                                state.styles.popper.width = `${state.rects.reference.width}px`;
                            },
                            effect: ({state}) => {
                                state.elements.popper.style.width = `${
                                    state.elements.reference.offsetWidth
                                }px`;
                            }
                        }, {
                            name: "preventOverflow",
                            options: {
                                mainAxis: false, // true by default
                            },
                        },
                    ]
                });
            }

            if (this.savedReplies.length === 0) {
                this.fetchSavedReplies();
            }

            // On some pages the savedReplyPopper does not open properly, this is a hack to get its position back in place
            this.$nextTick(function () {
                window.scrollBy(0, -1);
                window.scrollBy(0, 1);
            });
        },

        clearNotificationChannel (clearAdvancedEmailOptions = false) {
            this.selectedSavedReply = null;
            this.searchInput = null;

            // Keep cc, bcc and media when it was set.
            this.communicationChannel = {
                cc: this.communicationChannel.cc,
                bcc: this.communicationChannel.bcc,
                media: this.communicationChannel.media,
            };

            if (clearAdvancedEmailOptions) {
                delete this.communicationChannel.cc;
                delete this.communicationChannel.bcc;
                delete this.communicationChannel.media;
                return;
            }

            if (_.isEmpty(this.communicationChannel.cc)) {
                delete this.communicationChannel.cc;
            }

            if (_.isEmpty(this.communicationChannel.bcc)) {
                delete this.communicationChannel.bcc;
            }

            if (_.isEmpty(this.communicationChannel.media)) {
                delete this.communicationChannel.media;
            }
        },

        enterEditing () {
            if (this.isEditing) {
                this.leaveEditing();
                return;
            }
            this.$emit("toggle-edit-portal");
            this.displayPopup();
            this.isEditing = true;
        },

        leaveEditing () {
            this.$emit("toggle-edit-portal");
            this.hidePopup();
            this.isEditing = false;
        },

        checkboxToggledEvent () {
            if (!this.isSelected) {
                this.clearNotificationChannel(true);
                this.hidePopup();
            } else {
                this.communicationChannel = {
                    type: this.type,
                    saved_reply_id: null,
                    custom_body: null,
                    name: null,
                    cc: [],
                    bcc: [],
                    media: [],
                };
            }
        },

        toggleMultiAttachmentModal () {
            this.showMultiUploadModal = !this.showMultiUploadModal;
        },

        attachSelectedMedia (selectedAttachments) {
            if (!selectedAttachments) {
                toastr.error("Sorry, the selected media could not be attached! Please try again.");
                return;
            }

            selectedAttachments.forEach((attachment) => {
                if (!this.communicationChannel.media) {
                    this.communicationChannel.media = [];
                }

                if (this.communicationChannel?.media?.length < this.attachmentLimit) {
                    let index = this.communicationChannel.media.findIndex(media => (media.name === attachment.name || (media.uuid && attachment.uuid && media.uuid ===
                        attachment.uuid)));
                    if (index === -1) {
                        this.communicationChannel.media.push(attachment);
                    }
                } else {
                    toastr.error(`Only ${this.attachmentLimit} Attachments Are Allowed per Email!`);
                    return null;
                }
            });
            this.toggleMultiAttachmentModal(false);
        },

        removeSelectedAttachment (attachment) {
            let index = this.communicationChannel.media.findIndex(media => media.uuid === attachment.uuid);
            if (index !== -1) {
                this.communicationChannel.media.splice(index, 1);
            }
        },

        setIsSelected (selected) {
            this.isSelected = selected;
        },
    },
};
</script>
